import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Zap, Database, CheckCircle2, Edit2, Trash2, Search, ShieldCheck, X, Check } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import { I18nConfigCards } from "@/components/admin/i18n/I18nConfigCards";

export default function AdminI18n() {
  // Phase 81 — langues cibles = langues activées par l'admin (hors FR, langue de référence)
  const { languages } = useCurrency();
  const TARGET_LANGS = useMemo(() => languages.filter((l) => l.code !== "fr"), [languages]);
  const [stats, setStats] = useState({ total: 0, breakdown: [] });
  const [selected, setSelected] = useState({});
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const pollRef = useRef(null);
  useEffect(() => { setSelected((s) => Object.fromEntries(TARGET_LANGS.map((l) => [l.code, s[l.code] ?? true]))); }, [TARGET_LANGS]);

  // v2.0.14 — Human Review states
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewFilter, setReviewFilter] = useState({ target: "en", search: "", verified: "" });
  useEffect(() => { if (TARGET_LANGS.length && !TARGET_LANGS.some((l) => l.code === reviewFilter.target)) setReviewFilter((f) => ({ ...f, target: TARGET_LANGS[0].code })); }, [TARGET_LANGS, reviewFilter.target]);
  const [reviewData, setReviewData] = useState({ items: [], total: 0, offset: 0, limit: 50 });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState("");

  const load = async () => {
    try {
      const [{ data: s }, { data: j }] = await Promise.all([
        api.get("/i18n/translation-stats"),
        api.get("/admin/i18n/prewarm-jobs?limit=10"),
      ]);
      setStats(s);
      setJobs(j || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const startPrewarm = async () => {
    const targets = Object.keys(selected).filter((k) => selected[k]);
    if (targets.length === 0) { toast.error("Sélectionnez au moins une langue"); return; }
    try {
      const { data } = await api.post("/admin/i18n/prewarm", { targets });
      setJob({ id: data.job_id, total: data.total, targets: data.targets, checked: 0, translated: 0, status: "running" });
      toast.info(`Prewarm démarré · ${data.total} pairs à traiter`);
      pollRef.current = setInterval(async () => {
        try {
          const { data: st } = await api.get(`/admin/i18n/prewarm-status/${data.job_id}`);
          setJob(st);
          if (st.status !== "running") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (st.status === "done") {
              toast.success(`✓ Prewarm terminé · ${st.translated} traductions ajoutées · ${st.cached_hits} déjà cachées`);
            } else {
              toast.error(`✗ Échec : ${st.error || "erreur"}`);
            }
            await load();
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const progress = job?.total_strings && job?.targets?.length
    ? Math.round(100 * job.checked / (job.total_strings * job.targets.length))
    : 0;

  // v2.0.14 — Human Review handlers
  const loadReview = async () => {
    setReviewLoading(true);
    try {
      const params = new URLSearchParams();
      if (reviewFilter.target) params.set("target", reviewFilter.target);
      if (reviewFilter.search) params.set("search", reviewFilter.search);
      if (reviewFilter.verified) params.set("verified", reviewFilter.verified);
      params.set("limit", "50");
      params.set("offset", "0");
      const { data } = await api.get(`/admin/i18n/translations?${params}`);
      setReviewData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setReviewLoading(false); }
  };
  useEffect(() => { if (reviewOpen) loadReview(); /* eslint-disable-next-line */ }, [reviewOpen, reviewFilter.target, reviewFilter.verified]);

  const startEdit = (item) => {
    setEditingKey(item.key);
    setEditValue(item.translation);
  };

  const saveEdit = async (key) => {
    if (!editValue.trim()) { toast.error("Traduction vide"); return; }
    try {
      await api.patch(`/admin/i18n/translations/${key}`, { translation: editValue.trim() });
      toast.success("✓ Traduction validée (marquée human_verified)");
      setEditingKey(null);
      await loadReview();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteTranslation = async (key) => {
    if (!window.confirm("Supprimer cette traduction ? Elle sera re-générée par le LLM à la prochaine demande.")) return;
    try {
      await api.delete(`/admin/i18n/translations/${key}`);
      toast.success("Traduction supprimée");
      await loadReview();
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto space-y-6" data-testid="admin-i18n">
      <PageHeader title="Langues, devises & traductions" subtitle="Ajoutez des langues (traduction automatique par l'IA) et des devises (taux BCE quotidiens), puis pilotez le cache de traductions." />

      {/* Phase 81 — langues & devises administrables */}
      <I18nConfigCards />

      {/* Stats globales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="i18n-stats">
        <div className="p-4 bg-white border border-[#DEE2E6]">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#868E96] mb-1"><Database className="h-3 w-3" /> Total</div>
          <div className="font-display font-black text-3xl">{stats.total}</div>
          <div className="text-[10px] text-[#495057]">traductions en cache</div>
        </div>
        {TARGET_LANGS.map((l) => {
          const b = stats.breakdown.find((x) => x.target === l.code);
          return (
            <div key={l.code} className="p-4 bg-white border border-[#DEE2E6]">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#868E96] mb-1"><span className="text-base">{l.flag}</span> {l.label}</div>
              <div className="font-display font-black text-3xl">{b?.count || 0}</div>
              <div className="text-[10px] text-[#495057]">FR → {l.code.toUpperCase()}</div>
            </div>
          );
        })}
      </div>

      {/* Prewarm */}
      <div className="p-5 bg-white border border-[#DEE2E6]">
        <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
          <Zap className="h-5 w-5 text-[#F59E0B]" />
          Précharger le cache (200 chaînes UI critiques)
        </div>
        <div className="text-xs text-[#495057] mb-3">
          La liste couvre : navigation, actions (enregistrer, annuler...), landing hero, features, tarifs,
          inscription, login, dashboard chef, notifications, erreurs, footer. Environ 200 chaînes × N langues.
        </div>

        <div className="flex flex-wrap gap-2 mb-3" data-testid="i18n-lang-selector">
          {TARGET_LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              onClick={() => setSelected((s) => ({ ...s, [l.code]: !s[l.code] }))}
              data-testid={`i18n-lang-${l.code}`}
              className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold border-2 transition-colors ${selected[l.code] ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#DEE2E6] hover:border-[#0A0A0A]"}`}
            >
              <span className="text-base">{l.flag}</span> {l.label}
              {selected[l.code] && <CheckCircle2 className="h-3 w-3" />}
            </button>
          ))}
        </div>

        <Button onClick={startPrewarm} disabled={!!job?.status && job.status === "running"} data-testid="i18n-prewarm-btn" className="bg-[#F59E0B] text-white">
          <Zap className="h-4 w-4 mr-1" />
          {job?.status === "running" ? "En cours…" : "Lancer le prewarm"}
        </Button>

        {job && (
          <div className="mt-4 p-3 bg-[#F1F3F5] border-l-4 border-[#0EA5E9]" data-testid="i18n-prewarm-progress">
            <div className="flex items-center justify-between text-xs mb-1">
              <div>Job {job.id?.slice(0, 8)}… · <b>{job.status}</b> · {(job.targets || []).map((t) => t.toUpperCase()).join(", ")}</div>
              <div className="font-mono">{job.checked}/{(job.total_strings || 0) * (job.targets?.length || 1)} · +{job.translated || 0} nouveaux · {job.cached_hits || 0} cachés</div>
            </div>
            <div className="h-2 bg-white border border-[#DEE2E6]">
              <div className="h-full bg-[#059669] transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* v2.0.14 — Human Review */}
      <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="i18n-review">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="font-display font-bold text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#059669]" />
              Relecture humaine des traductions
            </div>
            <div className="text-xs text-[#495057]">Corrigez les traductions IA pour préserver la voix de la marque. Les entrées validées sont marquées <b>human_verified</b> et ne seront jamais écrasées par le LLM.</div>
          </div>
          <Button variant="outline" onClick={() => setReviewOpen(!reviewOpen)} data-testid="i18n-review-toggle">
            {reviewOpen ? <X className="h-4 w-4 mr-1" /> : <Edit2 className="h-4 w-4 mr-1" />}
            {reviewOpen ? "Fermer" : "Ouvrir"}
          </Button>
        </div>

        {reviewOpen && (
          <div>
            {/* Filtres */}
            <div className="flex flex-wrap gap-2 mb-3">
              <select
                value={reviewFilter.target}
                onChange={(e) => setReviewFilter({ ...reviewFilter, target: e.target.value })}
                className="px-3 py-2 text-xs border border-[#DEE2E6] bg-white"
                data-testid="i18n-review-lang"
              >
                {TARGET_LANGS.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
              </select>
              <select
                value={reviewFilter.verified}
                onChange={(e) => setReviewFilter({ ...reviewFilter, verified: e.target.value })}
                className="px-3 py-2 text-xs border border-[#DEE2E6] bg-white"
                data-testid="i18n-review-verified"
              >
                <option value="">Toutes</option>
                <option value="false">Non vérifiées (IA)</option>
                <option value="true">Vérifiées humain</option>
              </select>
              <div className="flex items-center gap-1 flex-1 min-w-[200px]">
                <Input
                  value={reviewFilter.search}
                  onChange={(e) => setReviewFilter({ ...reviewFilter, search: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") loadReview(); }}
                  placeholder="Rechercher un texte…"
                  className="text-xs"
                  data-testid="i18n-review-search"
                />
                <Button size="sm" onClick={loadReview} disabled={reviewLoading} data-testid="i18n-review-refresh">
                  <Search className="h-3 w-3" />
                </Button>
              </div>
            </div>

            <div className="text-xs text-[#495057] mb-2">{reviewData.total} résultats</div>

            {/* Table */}
            <div className="border border-[#DEE2E6] max-h-[500px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-[#F1F3F5] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-2">Original (FR)</th>
                    <th className="text-left px-2 py-2">Traduction ({reviewFilter.target.toUpperCase()})</th>
                    <th className="px-2 py-2 w-24">Statut</th>
                    <th className="px-2 py-2 w-32">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviewData.items.map((item) => (
                    <tr key={item.key} className="border-t border-[#DEE2E6] hover:bg-[#F8F9FA]" data-testid={`i18n-review-row-${item.key}`}>
                      <td className="px-2 py-2 align-top">{item.text?.slice(0, 150)}</td>
                      <td className="px-2 py-2 align-top">
                        {editingKey === item.key ? (
                          <Input
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") saveEdit(item.key); }}
                            className="text-xs"
                            autoFocus
                            data-testid={`i18n-review-edit-${item.key}`}
                          />
                        ) : (
                          <span>{item.translation?.slice(0, 150)}</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center align-top">
                        {item.human_verified ? (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#059669] text-white px-1.5 py-0.5">
                            <ShieldCheck className="h-2.5 w-2.5" /> Vérifié
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold uppercase tracking-wider bg-[#F59E0B] text-white px-1.5 py-0.5">
                            IA
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right align-top">
                        {editingKey === item.key ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" onClick={() => saveEdit(item.key)} data-testid={`i18n-review-save-${item.key}`} className="bg-[#059669] text-white h-6 px-2">
                              <Check className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingKey(null)} className="h-6 px-2">
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => startEdit(item)} data-testid={`i18n-review-edit-btn-${item.key}`} className="h-6 px-2">
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteTranslation(item.key)} data-testid={`i18n-review-del-${item.key}`} className="h-6 px-2 text-[#E60000] hover:bg-[#E60000] hover:text-white">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {reviewData.items.length === 0 && !reviewLoading && (
                    <tr><td colSpan={4} className="text-center text-[#868E96] py-6">Aucune traduction pour ces filtres</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Historique jobs */}
      {jobs.length > 0 && (
        <div className="p-5 bg-white border border-[#DEE2E6]">
          <div className="font-display font-bold text-lg mb-3">Historique ({jobs.length})</div>
          <div className="divide-y divide-[#DEE2E6]">
            {jobs.map((j) => (
              <div key={j.id} className="p-2 grid grid-cols-1 md:grid-cols-5 gap-2 text-xs items-center">
                <div className="font-mono text-[10px]">{j.id.slice(0, 8)}…</div>
                <div className="text-[10px] text-[#495057]">{j.started_at?.slice(0, 16).replace("T", " ")}</div>
                <div className={`text-[10px] font-bold uppercase tracking-wider inline-block px-1.5 py-0.5 ${j.status === "done" ? "bg-[#059669] text-white" : j.status === "error" ? "bg-[#E60000] text-white" : "bg-[#F59E0B] text-white"}`}>{j.status}</div>
                <div>{(j.targets || []).join(", ")}</div>
                <div className="text-right font-mono text-[10px]">{j.translated || 0} + {j.cached_hits || 0} cache</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
